import {
  DataStructure,
  DomCategoryTree,
} from '@/services/categories/category.interface';
import { modelFieldToDtoKey } from '@/core/models/organization-model-slug';
import { DataStructureOptionsDto, DynamicFieldDto, UpdateDataStructureOptionsDto } from '@/shared/openapi';

export interface NormalizedDataStructureOptionsDto extends Omit<
  DataStructureOptionsDto,
  | 'field_name'
  | 'dynamic_field_id'
  | 'association_name'
  | 'association_field'
  | 'association_model'
  | 'table_column_index'
  | 'table_column_min_width'
  | 'modal_index'
  | 'modal_width'
  | 'data_path'
  | 'projection_path'
> {
  field_name?: string;
  dynamic_field_id?: string;
  association_name?: string;
  association_field?: string;
  association_model?: string;
  table_column_index?: number;
  table_column_min_width?: number;
  /** Preferred column width (may arrive from API before OpenAPI client regen). */
  table_column_width?: number;
  modal_index?: number;
  modal_width?: number;
  data_path?: string;
  projection_path?: string;
}

/** Update payload including fields not yet on the generated OpenAPI DTO. */
export type UpdateDataStructureOptionsPayload = UpdateDataStructureOptionsDto & {
  table_column_width?: number;
};

function asOptionalString(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  return typeof value === 'string' ? value : String(value);
}

function asOptionalNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Max DSO fields marked searchable per model (API-enforced). */
export const MAX_SEARCHABLE_FIELDS = 5;

/**
 * Resolve DF type/structure from the option payload, falling back to the live
 * data-structure tree when `dynamic_field` was not included on the DSO row.
 */
export function resolveOptionDynamicField(
  option: Pick<
    NormalizedDataStructureOptionsDto,
    'dynamic_field' | 'dynamic_field_id' | 'field_name'
  >,
  dataStructure?: DataStructure | null
): DynamicFieldDto | undefined {
  if (option.dynamic_field) return option.dynamic_field;
  if (!dataStructure) return undefined;

  const byId = option.dynamic_field_id?.trim();
  if (byId) {
    const dynamicFields = dataStructure['dynamic_fields'] as
      | Record<string, DynamicFieldDto>
      | undefined;
    if (dynamicFields && typeof dynamicFields === 'object') {
      const match = Object.values(dynamicFields).find(
        df => isDynamicFieldLike(df) && String(df.id) === byId
      );
      if (match) return match;
    }
  }

  const name = option.field_name?.trim();
  return name ? findDynamicFieldByKeyOrName(dataStructure, name) : undefined;
}

/**
 * Whether free-text list `search` may target this option.
 * Mirrors API `isTextualSearchableOption` (string DF/association; system string/number, not multi).
 */
export function isTextualSearchableOption(
  option: Pick<
    NormalizedDataStructureOptionsDto,
    | 'option_type'
    | 'field_name'
    | 'field_type'
    | 'association_name'
    | 'association_field'
    | 'is_multi_value'
    | 'dynamic_field'
    | 'dynamic_field_id'
  >,
  dataStructure?: DataStructure | null
): boolean {
  const resolvedDf = resolveOptionDynamicField(option, dataStructure);
  const fieldType = String(resolvedDf?.type ?? option.field_type ?? '');
  const isString = fieldType === 'string';
  const isNumber = fieldType === 'number';

  if (
    option.option_type === DataStructureOptionsDto.OptionTypeEnum.DynamicField
  ) {
    const structure =
      resolvedDf?.structure ?? DynamicFieldDto.StructureEnum.SingleValue;
    return (
      isString && structure === DynamicFieldDto.StructureEnum.SingleValue
    );
  }

  if (
    option.option_type === DataStructureOptionsDto.OptionTypeEnum.Association
  ) {
    return (
      !!option.association_name?.trim() &&
      !!option.association_field?.trim() &&
      isString
    );
  }

  if (option.option_type !== DataStructureOptionsDto.OptionTypeEnum.System) {
    return false;
  }

  const fieldName = option.field_name?.trim();
  if (!fieldName || option.is_multi_value) return false;
  return isString || isNumber;
}

export function normalizeDataStructureOption(
  option: Omit<
    DataStructureOptionsDto,
    'is_multi_value' | 'is_searchable' | 'updated_at'
  > & {
    is_multi_value?: boolean;
    is_searchable?: boolean;
    updated_at?: number;
  }
): NormalizedDataStructureOptionsDto {
  const raw = option as typeof option & {
    table_column_width?: number | null;
  };
  return {
    ...option,
    updated_at: option.updated_at ?? 0,
    is_multi_value: option.is_multi_value ?? false,
    is_searchable: option.is_searchable === true,
    field_name: asOptionalString(option.field_name),
    dynamic_field_id: asOptionalString(option.dynamic_field_id),
    association_name: asOptionalString(option.association_name),
    association_field: asOptionalString(option.association_field),
    association_model: asOptionalString(option.association_model),
    table_column_index: asOptionalNumber(option.table_column_index),
    table_column_min_width: asOptionalNumber(option.table_column_min_width),
    table_column_width: asOptionalNumber(raw.table_column_width),
    modal_index: asOptionalNumber(option.modal_index),
    modal_width: asOptionalNumber(option.modal_width),
  };
}

export interface DataStructureOptionFieldIdentity {
  fieldName: string;
  dynamicFieldId?: string;
}

/** Canonical camelCase form for comparing tree keys with API `field_name` values. */
export function normalizeDataStructureFieldName(name: string): string {
  return name.replace(/_([a-z0-9])/gi, (_, char: string) => char.toUpperCase());
}

/**
 * Converts a raw API field/association name to Title Case with spaces for display.
 * Handles snake_case, kebab-case, camelCase, and plain lowercase strings.
 * Examples: "types" → "Types", "some_field_name" → "Some Field Name",
 *           "mainAddress" → "Main Address", "mainContactId" → "Main Contact Id"
 */
export function toPascalCase(str: string): string {
  if (!str) return str;
  // Split on underscores/hyphens and also on camelCase boundaries
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2') // insert space before each uppercase run in camelCase
    .split(/[_\-\s]+/) // then split on separators
    .map(w => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .filter(Boolean)
    .join(' ');
}

/** Last segment of a dotted association field (`product.name` → `name`). */
export function associationFieldLeaf(
  fieldName: string | null | undefined
): string {
  const trimmed = fieldName?.trim() ?? '';
  if (!trimmed) return '';
  const separator = trimmed.lastIndexOf('.');
  return separator === -1 ? trimmed : trimmed.slice(separator + 1);
}

/** First segment of a dotted association field (`product.name` → `product`). */
export function associationFieldNestPrefix(
  fieldName: string | null | undefined
): string | null {
  const trimmed = fieldName?.trim() ?? '';
  if (!trimmed) return null;
  const separator = trimmed.indexOf('.');
  return separator === -1 ? null : trimmed.slice(0, separator);
}

const FILE_OR_CATEGORY_FIELD_NAMES = new Set(['files', 'categories', 'images']);

/** FK / pointer row on an association (sort order). */
export function isAssociationPointerField(
  associationField: string | null | undefined
): boolean {
  const normalized = associationFieldLeaf(associationField).toLowerCase();
  return normalized === 'id' || normalized === 'identifier';
}

/** Collection `id` row on the selected association (`suppliers.id`), not nested hops. */
export function isCollectionPointerField(
  associationField: string | null | undefined
): boolean {
  const trimmed = associationField?.trim() ?? '';
  if (!trimmed || associationFieldNestPrefix(trimmed)) return false;
  return trimmed.toLowerCase() === 'id';
}

/**
 * Id/FK holders and file/category key arrays — no validation rules in the DSO UI.
 * Nested scalars such as `taxId` still show validation.
 */
export function optionHoldsIdsOrFileKeys(
  option: NormalizedDataStructureOptionsDto
): boolean {
  const leaf = associationFieldLeaf(
    option.association_field || option.field_name
  );
  if (!leaf) return false;
  const lower = leaf.toLowerCase();
  if (isAssociationPointerField(leaf)) return true;
  if (
    FILE_OR_CATEGORY_FIELD_NAMES.has(leaf) ||
    FILE_OR_CATEGORY_FIELD_NAMES.has(lower)
  ) {
    return true;
  }
  if (!option.association_field?.trim()) {
    const fieldName = option.field_name?.trim() ?? '';
    if (/Id$|_id$/.test(fieldName)) return true;
  }
  return false;
}

/** Validation block on option cards: skip DFs and Id/Files/Categories/FK holders. */
export function shouldShowOptionValidationRules(
  option: NormalizedDataStructureOptionsDto
): boolean {
  if (presentsAsDynamicFieldOption(option)) return false;
  if (optionHoldsIdsOrFileKeys(option)) return false;
  return true;
}

export interface SplitAssociationOptionCards<
  T extends { association_field?: string | null },
> {
  pointer: T | undefined;
  direct: T[];
  nestedByPrefix: Map<string, T[]>;
}

/**
 * Pointer `id` row vs direct related-model fields vs one-hop nested
 * associations (`product.name`).
 */
export function splitAssociationOptionCards<
  T extends { association_field?: string | null },
>(cards: T[]): SplitAssociationOptionCards<T> {
  const pointer = cards.find(card =>
    isCollectionPointerField(card.association_field)
  );
  const nestedByPrefix = new Map<string, T[]>();
  const direct: T[] = [];

  for (const card of cards) {
    if (pointer && card === pointer) continue;
    const prefix = associationFieldNestPrefix(card.association_field);
    if (prefix) {
      const list = nestedByPrefix.get(prefix) ?? [];
      list.push(card);
      nestedByPrefix.set(prefix, list);
    } else {
      direct.push(card);
    }
  }

  return { pointer, direct, nestedByPrefix };
}

/**
 * System / org-required checkboxes: direct fields, and the association `id`
 * row. Dynamic fields use `is_required` on the field itself.
 */
export function shouldShowRequiredOptionFields(
  option: NormalizedDataStructureOptionsDto
): boolean {
  if (
    isDynamicFieldOption(option) ||
    isDynamicListItemAssociationOption(option)
  ) {
    return false;
  }

  if (option.association_name?.trim()) {
    return (
      associationFieldLeaf(option.association_field).toLowerCase() === 'id'
    );
  }

  return true;
}

export function dataStructureFieldNamesEqual(
  left: string | null | undefined,
  right: string | null | undefined
): boolean {
  if (left == null || right == null) return false;
  if (left === right) return true;
  const normalizedLeft = normalizeDataStructureFieldName(left);
  const normalizedRight = normalizeDataStructureFieldName(right);
  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.toLowerCase() === normalizedRight.toLowerCase()
  );
}

function isDynamicFieldLike(value: unknown): value is DynamicFieldDto {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    ('id' in value ||
      'entity_type' in value ||
      'structure' in value ||
      'list_items' in value)
  );
}

function dynamicFieldIdFrom(value: unknown): string | undefined {
  if (!isDynamicFieldLike(value) || value.id == null || value.id === '') {
    return undefined;
  }
  return String(value.id);
}

function findDynamicFieldByKeyOrName(
  dataStructure: DataStructure,
  key: string
): DynamicFieldDto | undefined {
  const dynamicFields = dataStructure['dynamic_fields'] as
    | Record<string, DynamicFieldDto>
    | undefined;
  if (!dynamicFields || typeof dynamicFields !== 'object') {
    return undefined;
  }

  const direct = dynamicFields[key];
  if (isDynamicFieldLike(direct)) {
    return direct;
  }

  return Object.values(dynamicFields).find(
    df => isDynamicFieldLike(df) && dataStructureFieldNamesEqual(df.name, key)
  );
}

/** Resolve tree selection to API `field_name` / `dynamic_field_id` used in options rows. */
export function resolveDataStructureOptionFieldIdentity(
  attribute: string,
  dataStructure: DataStructure
): DataStructureOptionFieldIdentity {
  if (attribute.startsWith('dynamic_fields.')) {
    const key = attribute.replace('dynamic_fields.', '');
    const dynamicField = findDynamicFieldByKeyOrName(dataStructure, key);
    return {
      fieldName: dynamicField?.name ?? key,
      dynamicFieldId: dynamicFieldIdFrom(dynamicField),
    };
  }

  const topLevelField = (dataStructure as Record<string, unknown>)[attribute];
  const topLevelId = dynamicFieldIdFrom(topLevelField);
  if (topLevelId) {
    const name =
      isDynamicFieldLike(topLevelField) && topLevelField.name?.trim()
        ? topLevelField.name
        : attribute;
    return { fieldName: name, dynamicFieldId: topLevelId };
  }

  const nested = findDynamicFieldByKeyOrName(dataStructure, attribute);
  return {
    fieldName: nested?.name ?? attribute,
    dynamicFieldId: dynamicFieldIdFrom(nested),
  };
}

export function dataStructureOptionMatchesAttribute(
  option: NormalizedDataStructureOptionsDto,
  identity: DataStructureOptionFieldIdentity
): boolean {
  if (identity.dynamicFieldId && option.dynamic_field_id) {
    return option.dynamic_field_id === identity.dynamicFieldId;
  }

  if (identity.dynamicFieldId) {
    return false;
  }

  if (!dataStructureFieldNamesEqual(option.field_name, identity.fieldName)) {
    return false;
  }

  // Tree keys like `status` are often a column type string, not a DF payload.
  // Still show the DF / list-item rows that share that field name.
  return true;
}

export const DYNAMIC_FIELDS_PATH = 'dynamic_fields';

const DYNAMIC_LIST_ITEM_MODEL = 'dynamic_list_item';
const LIST_ITEM_VALUE_FIELD = 'value';

/** Association rows that fan out a column-backed list DF onto `dynamic_list_item`. */
export function isDynamicListItemAssociationOption(
  option: NormalizedDataStructureOptionsDto
): boolean {
  return option.association_model?.trim() === DYNAMIC_LIST_ITEM_MODEL;
}

/**
 * The only DLI sub-field catalogues should show: the list item label.
 * Visibility / column width on this row is what the API uses for table + details.
 */
export function isListItemValueAssociationOption(
  option: NormalizedDataStructureOptionsDto
): boolean {
  return (
    isDynamicListItemAssociationOption(option) &&
    option.association_field?.trim().toLowerCase() === LIST_ITEM_VALUE_FIELD
  );
}

/**
 * SYSTEM column-backed list rows (`types`, `qcStatuses`): the selection lives
 * on the entity column, linked to a DF list via `association_model`, with no
 * BelongsTo fan-out (`association_field` empty). Present like a dynamic field.
 */
export function isColumnBoundListSystemOption(
  option: NormalizedDataStructureOptionsDto
): boolean {
  return (
    isDynamicListItemAssociationOption(option) &&
    !option.association_field?.trim() &&
    !option.association_name?.trim()
  );
}

/** True when the DSO editor / table should present this row like a JSON dynamic field. */
export function presentsAsDynamicFieldOption(
  option: NormalizedDataStructureOptionsDto
): boolean {
  return (
    isDynamicFieldOption(option) ||
    isListItemValueAssociationOption(option) ||
    isColumnBoundListSystemOption(option)
  );
}

/**
 * Column-backed list DFs sync as many ASSOCIATION rows (id, value, translations, …).
 * Keep only `value` so the editor matches a plain dynamic field.
 */
export function collapseDynamicListItemAssociationOptions(
  options: NormalizedDataStructureOptionsDto[]
): NormalizedDataStructureOptionsDto[] {
  const listItemRows = options.filter(isDynamicListItemAssociationOption);
  if (!listItemRows.length) return options;

  const valueRow = listItemRows.find(isListItemValueAssociationOption);
  if (!valueRow) return options;

  const otherRows = options.filter(
    option => !isDynamicListItemAssociationOption(option)
  );
  return [...otherRows, valueRow];
}

export function selectDataStructureOptionsForAttribute(
  options: NormalizedDataStructureOptionsDto[],
  identity: DataStructureOptionFieldIdentity
): NormalizedDataStructureOptionsDto[] {
  return collapseDynamicListItemAssociationOptions(
    options.filter(option =>
      dataStructureOptionMatchesAttribute(option, identity)
    )
  );
}

export interface AttributeOptionFlags {
  visibleInRowDetails: boolean;
  visibleInTableColumn: boolean;
  systemRequired: boolean;
  organizationRequired: boolean;
  searchable: boolean;
}

/** OR-aggregate DSO flags for an attributes-list row (field, DF, or association). */
export function aggregateAttributeOptionFlags(
  options: NormalizedDataStructureOptionsDto[]
): AttributeOptionFlags {
  return {
    visibleInRowDetails: options.some(
      option => option.is_visible_in_row_details
    ),
    visibleInTableColumn: options.some(
      option => option.is_visible_in_table_column
    ),
    systemRequired: options.some(option => option.is_system_required),
    organizationRequired: options.some(
      option => option.is_organization_required
    ),
    searchable: options.some(option => option.is_searchable),
  };
}

/**
 * DSO rows are org-wide (one per field), so each field is editable in only
 * one category to avoid duplicate edits:
 * - All (root): every field
 * - a child category: only dynamic fields owned by that category
 *   (not inherited / from_parent), which never appear under All
 */
export function canEditDataStructureOptionsForCategory(params: {
  isSelectedCategoryRoot: boolean;
  isOwnDynamicField: boolean;
}): boolean {
  return params.isSelectedCategoryRoot || params.isOwnDynamicField;
}

/**
 * Association pointer selections group child fields under `association_name`.
 * Dynamic-field rows reuse `association_name = 'dynamicFields'` as a marker,
 * not as a real association — they must stay in the direct group.
 * Column-backed list DFs are also shown as a single dynamic-field card.
 */
export function shouldGroupAsAssociation(
  option: NormalizedDataStructureOptionsDto
): boolean {
  return (
    !isDynamicFieldOption(option) &&
    !isDynamicListItemAssociationOption(option) &&
    !!option.association_name?.trim()
  );
}

/**
 * BelongsToMany DSO sub-fields: the API sets `is_multi_value` and keys the row
 * by association `as` (`suppliers.name`). Table cells join related entities.
 */
export function isToManyAssociationOption(
  option: NormalizedDataStructureOptionsDto
): boolean {
  return (
    option.is_multi_value === true &&
    !!option.association_name?.trim() &&
    !!option.association_field?.trim()
  );
}

function collectTreeValues(nodes: DomCategoryTree[], into: Set<string>): void {
  for (const node of nodes) {
    if (node.value != null && node.value !== '') {
      into.add(String(node.value));
    }
    if (node.items?.length) {
      collectTreeValues(node.items, into);
    }
  }
}

/**
 * Unique attributes-list values for BelongsToMany DSO groups (`suppliers`).
 */
export function toManyAssociationAttributeValues(
  options: NormalizedDataStructureOptionsDto[]
): string[] {
  const values = new Set<string>();
  for (const option of options) {
    if (!isToManyAssociationOption(option)) continue;
    const name = option.association_name?.trim();
    if (!name) continue;
    values.add(modelFieldToDtoKey(name));
  }
  return [...values].sort((a, b) => a.localeCompare(b));
}

/**
 * Inserts missing BelongsToMany attributes (Supplier, Client, …) into the
 * attributes tree. Skips keys already present from GET /data-structure.
 */
export function appendToManyAssociationAttributeNodes(
  tree: DomCategoryTree[],
  options: NormalizedDataStructureOptionsDto[],
  resolveLabel: (value: string) => string,
  insertAt = tree.length
): DomCategoryTree[] {
  if (!tree.length) return tree;
  const existing = new Set<string>();
  collectTreeValues(tree, existing);
  const existingNormalized = new Set(
    [...existing].map(value => normalizeDataStructureFieldName(value))
  );

  const extras: DomCategoryTree[] = [];
  for (const value of toManyAssociationAttributeValues(options)) {
    if (existing.has(value)) continue;
    if (existingNormalized.has(normalizeDataStructureFieldName(value))) {
      continue;
    }
    extras.push({ label: resolveLabel(value), value });
  }
  if (!extras.length) return tree;
  const at = Math.max(0, Math.min(insertAt, tree.length));
  return [...tree.slice(0, at), ...extras, ...tree.slice(at)];
}

export function isDynamicFieldOption(
  option: NormalizedDataStructureOptionsDto
): boolean {
  if (
    option.option_type === DataStructureOptionsDto.OptionTypeEnum.DynamicField
  ) {
    return true;
  }
  const assoc = option.association_name?.trim();
  if (assoc === 'dynamicFields' || assoc === DYNAMIC_FIELDS_PATH) {
    return true;
  }
  const path = option.data_path?.trim() || option.projection_path?.trim() || '';
  return (
    path === DYNAMIC_FIELDS_PATH || path.startsWith(`${DYNAMIC_FIELDS_PATH}.`)
  );
}

/**
 * Unique JSONB lookup path for a dynamic-field option (`dynamic_fields.<name>`).
 * `projection_path` is always `dynamic_fields` and cannot identify a single column.
 */
export function resolveDynamicFieldDataPath(
  option: NormalizedDataStructureOptionsDto
): string | null {
  const path = option.data_path?.trim();
  if (path && path !== DYNAMIC_FIELDS_PATH) {
    return path;
  }
  const name = option.field_name?.trim();
  return name ? `${DYNAMIC_FIELDS_PATH}.${name}` : null;
}

/** Dynamic-field names that belong in row details (not already shown as table columns). */
export function dynamicFieldNamesForRowDetails(
  options: NormalizedDataStructureOptionsDto[]
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const option of options) {
    if (!isDynamicFieldOption(option)) continue;
    if (option.is_visible_in_table_column) continue;
    if (!option.is_visible_in_row_details) continue;
    const name = option.field_name?.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}
